import { motion } from "framer-motion";
import { Star, Quote } from "lucide-react";

const TestimonialsSection = () => {
  const testimonials = [
    {
      name: "Sarah Chen",
      role: "Owner, Sunrise Café",
      image: "https://images.unsplash.com/photo-1558438278-39f8bee9f0a3?w=100&h=100&fit=crop&crop=face",
      quote: "We stopped running weekly discounts and started with PointWise. Three months later, our repeat customer rate went from 23% to 58%. The psychology really works.",
      rating: 5,
    },
    {
      name: "David Martinez",
      role: "Manager, Urban Threads",
      image: "https://images.unsplash.com/photo-1540569014015-19a7be504e3a?w=100&h=100&fit=crop&crop=face",
      quote: "My customers actually talk about their points now. They come in saying 'I'm so close to my reward!' That never happened with our old discount cards.",
      rating: 5,
    },
    {
      name: "Marcus Johnson",
      role: "Owner, Flavor House Restaurant",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face",
      quote: "The 3% model made it risk-free to try. Within the first month, we saw clear patterns in customer behavior. Now I can actually plan for busy days.",
      rating: 5,
    },
  ];

  return (
    <section 
      className="section-padding bg-gradient-to-br from-slate-50 to-indigo-50"
      data-testid="testimonials-section"
    >
      <div className="container-main">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center max-w-2xl mx-auto mb-16"
        >
          <span className="inline-block px-4 py-1.5 rounded-full bg-indigo-50 text-indigo-600 text-sm font-medium mb-4 border border-indigo-100">
            Success Stories
          </span>
          <h2 
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="testimonials-headline"
          >
            Businesses Love PointWise
          </h2>
          <p className="text-lg text-slate-600">
            Real stories from merchants who switched from discounts to loyalty.
          </p>
        </motion.div>

        {/* Testimonials grid */}
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={testimonial.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="testimonial-card relative"
              data-testid={`testimonial-${index}`}
            >
              {/* Quote icon */}
              <div className="absolute -top-3 right-6 w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                <Quote className="w-4 h-4 text-indigo-600" />
              </div>

              {/* Rating */}
              <div className="flex gap-1 mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 text-amber-400 fill-amber-400" />
                ))}
              </div>

              {/* Quote */}
              <p className="text-slate-600 mb-6 leading-relaxed">
                "{testimonial.quote}"
              </p>

              {/* Author */}
              <div className="flex items-center gap-3 pt-4 border-t border-slate-100">
                <img
                  src={testimonial.image}
                  alt={testimonial.name}
                  className="w-12 h-12 rounded-full object-cover"
                />
                <div>
                  <p className="font-semibold text-slate-900">{testimonial.name}</p>
                  <p className="text-sm text-slate-500">{testimonial.role}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="mt-16"
        >
          <div className="bg-white rounded-3xl p-8 lg:p-12 shadow-sm border border-slate-100">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                { value: "500+", label: "Active Businesses" },
                { value: "50K+", label: "Rewards Redeemed" },
                { value: "2.7x", label: "Avg. Visit Increase" },
                { value: "94%", label: "Customer Satisfaction" },
              ].map((stat, index) => (
                <div key={stat.label} className="text-center">
                  <p 
                    className="text-3xl lg:text-4xl font-bold gradient-text mb-2"
                    style={{ fontFamily: 'Outfit, sans-serif' }}
                  >
                    {stat.value}
                  </p>
                  <p className="text-sm text-slate-500">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
